import React, { useState, useMemo } from 'react';
import { 
  BarChart3, 
  Search, 
  Calendar,
  ArrowRightLeft,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Scale,
  Filter
} from 'lucide-react';
import { JournalEntry, AccountingAccount, AccountingCategory } from '../../types';
import { format, parseISO, isWithinInterval, startOfMonth, endOfMonth, isValid } from 'date-fns';
import { es } from 'date-fns/locale';
import { safeFormat } from '../../utils/dateUtils';
import { useFirebase } from '../FirebaseProvider';
import { updateAccountingAccount } from '../../services/accountingService';

interface ReportsViewProps {
  entries: JournalEntry[];
  accounts: AccountingAccount[];
}

export const ReportsView: React.FC<ReportsViewProps> = ({ entries, accounts }) => {
  const { user } = useFirebase();
  const [reportType, setReportType] = useState<'BALANCE' | 'P&L'>('BALANCE');
  const [periodType, setPeriodType] = useState<'MONTH' | 'RANGE' | 'CUMULATIVE'>('MONTH');
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [isFixingCategories, setIsFixingCategories] = useState(false);
  const [showFixCategoriesConfirm, setShowFixCategoriesConfirm] = useState(false);
  const [fixCategoriesError, setFixCategoriesError] = useState<string | null>(null);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PY', { style: 'currency', currency: 'PYG' }).format(amount);
  };

  const getFilteredEntries = useMemo(() => {
    if (periodType === 'CUMULATIVE') return entries;
    
    return entries.filter(e => {
      if (periodType === 'MONTH') {
        // Safe string comparison for YYYY-MM
        return e.date.startsWith(selectedMonth);
      }
      if (periodType === 'RANGE') {
        if (!dateRange.start || !dateRange.end) return true;
        // String comparison is reliable for YYYY-MM-DD
        return e.date >= dateRange.start && e.date <= dateRange.end;
      }
      return true;
    });
  }, [entries, periodType, selectedMonth, dateRange]);

  // Financial Logic
  const financialData = useMemo(() => {
    const getChildren = (parentId: string | null) => accounts.filter(a => a.parentId === parentId);
    const calculateRecursiveBalance = (acc: AccountingAccount, balancesMap: Record<string, number>): number => {
      let sum = balancesMap[acc.id] || 0;
      const children = getChildren(acc.id);
      for (const child of children) {
        sum += calculateRecursiveBalance(child, balancesMap);
      }
      return sum;
    };

    // Apply entries for Balance Sheet (Cumulative up to the end of selected period)
    const entriesForBalance = entries.filter(e => {
      if (periodType === 'CUMULATIVE') return true;
      
      let limitDateStr = '';
      if (periodType === 'MONTH') {
        // For month filter, Balance Sheet state is up to the last day of that specific year-month
        if (/^\d{4}-\d{2}$/.test(selectedMonth)) {
          const targetDate = parseISO(`${selectedMonth}-01`);
          if (isValid(targetDate)) {
            limitDateStr = format(endOfMonth(targetDate), 'yyyy-MM-dd');
          } else {
            limitDateStr = '9999-12-31';
          }
        } else {
          limitDateStr = '9999-12-31'; // Safe fallback if input is invalid/empty
        }
      } else {
        // For range, up to the end of the range
        limitDateStr = dateRange.end || '9999-12-31';
      }
      return e.date <= limitDateStr;
    });

    const entriesForPnL = getFilteredEntries;

    const calcBalancesBS = (entriesList: JournalEntry[]) => {
      const balances: Record<string, number> = {};
      accounts.forEach(acc => { balances[acc.id] = acc.initialBalance || 0; });
      entriesList.forEach(entry => {
        entry.lines.forEach(line => {
          const acc = accounts.find(a => a.id === line.accountId);
          if (!acc) return;
          const isNaturalDebit = ['ACTIVO', 'EGRESO'].includes(acc.category);
          if (isNaturalDebit) {
            balances[line.accountId] = (balances[line.accountId] || 0) + (line.debit - line.credit);
          } else {
            balances[line.accountId] = (balances[line.accountId] || 0) + (line.credit - line.debit);
          }
        });
      });
      return balances;
    };

    const balancesBS = calcBalancesBS(entriesForBalance);

    const rawPnlBalancesVariations: Record<string, number> = {};
    accounts.forEach(acc => {
      if (acc.category === 'INGRESO' || acc.category === 'EGRESO') {
        let variation = 0;
        entriesForPnL.forEach(e => {
          e.lines.filter(l => l.accountId === acc.id).forEach(l => {
            if (acc.category === 'INGRESO') variation += (l.credit - l.debit);
            else variation += (l.debit - l.credit);
          });
        });
        rawPnlBalancesVariations[acc.id] = variation;
      }
    });

    // Group by category for P&L
    const pnlResult = {
      ingresos: 0,
      egresos: 0,
      items: [] as any[]
    };

    accounts.forEach(acc => {
      if (acc.category === 'INGRESO' || acc.category === 'EGRESO') {
        const balance = calculateRecursiveBalance(acc, rawPnlBalancesVariations);
        if (balance !== 0 || !acc.isSelectable) {
          pnlResult.items.push({ ...acc, balance });
        }
      }
    });

    pnlResult.ingresos = pnlResult.items.filter(i => i.category === 'INGRESO' && !i.parentId).reduce((s, i) => s + i.balance, 0);
    pnlResult.egresos = pnlResult.items.filter(i => i.category === 'EGRESO' && !i.parentId).reduce((s, i) => s + i.balance, 0);

    const netIncome = pnlResult.ingresos - pnlResult.egresos;

    // Group by category for Balance Sheet
    const bsResult = {
      activo: 0,
      pasivo: 0,
      patrimonio: 0,
      items: [] as any[]
    };

    accounts.forEach(acc => {
      if (['ACTIVO', 'PASIVO', 'PATRIMONIO'].includes(acc.category)) {
        const balance = calculateRecursiveBalance(acc, balancesBS);
        if (balance !== 0 || !acc.isSelectable) {
          bsResult.items.push({ ...acc, balance });
        }
      }
    });

    bsResult.activo = bsResult.items.filter(i => i.category === 'ACTIVO' && !i.parentId).reduce((s, i) => s + i.balance, 0);
    bsResult.pasivo = bsResult.items.filter(i => i.category === 'PASIVO' && !i.parentId).reduce((s, i) => s + i.balance, 0);
    bsResult.patrimonio = bsResult.items.filter(i => i.category === 'PATRIMONIO' && !i.parentId).reduce((s, i) => s + i.balance, 0);

    // In Balance sheet, add the Net Income (from start to period end) to Equity
    let cumulativeNetIncome = 0;
    
    // Calculate initial balances for PnL
    let initialIncome = 0;
    let initialExpense = 0;
    accounts.forEach(a => {
      if (a.category === 'INGRESO') initialIncome += (a.initialBalance || 0);
      if (a.category === 'EGRESO') initialExpense += (a.initialBalance || 0);
    });
    
    cumulativeNetIncome += (initialIncome - initialExpense);

    entriesForBalance.forEach(e => {
        e.lines.forEach(l => {
            const acc = accounts.find(a => a.id === l.accountId);
            if (acc?.category === 'INGRESO') cumulativeNetIncome += (l.credit - l.debit);
            if (acc?.category === 'EGRESO') cumulativeNetIncome -= (l.debit - l.credit);
        });
    });

    // Diagnostic Calculation
    let initialActivo = 0, initialPasivo = 0, initialPatrimonio = 0, initialIngreso = 0, initialEgreso = 0;
    let accountsWithoutCategory: string[] = [];
    accounts.forEach(a => {
      // initialBalance is only considered for the lowest level accounts or where defined (we must be careful not to double count if parents have it, but standard implies only leaves have it or they don't overlap)
      // Actually, calculateRecursiveBalance adds the values up. Let's just track root level nodes or rely on category.
      // But only accounts with no children should really hold the initial balance? No, we can just sum based on categories.
      // Wait, an account's parent might also have a category. If both have category, we double count.
      // Let's only sum initial balances.
      if (a.category === 'ACTIVO') initialActivo += (a.initialBalance || 0);
      else if (a.category === 'PASIVO') initialPasivo += (a.initialBalance || 0);
      else if (a.category === 'PATRIMONIO') initialPatrimonio += (a.initialBalance || 0);
      else if (a.category === 'INGRESO') initialIngreso += (a.initialBalance || 0);
      else if (a.category === 'EGRESO') initialEgreso += (a.initialBalance || 0);
      
      if (a.category !== 'INGRESO' && a.category !== 'EGRESO' && a.category !== 'ACTIVO' && a.category !== 'PASIVO' && a.category !== 'PATRIMONIO') {
        if (!a.parentId) accountsWithoutCategory.push(a.name); // only flag root level if missing
      }
    });
    
    // Check if initial balances alone are unbalanced
    const setupImbalance = Math.abs(initialActivo - (initialPasivo + initialPatrimonio + initialIngreso - initialEgreso)) > 0.01;

    const unbalancedEntriesInfo = entriesForBalance.filter(e => {
      const debit = e.lines.reduce((s, l) => s + l.debit, 0);
      const credit = e.lines.reduce((s, l) => s + l.credit, 0);
      return Math.abs(debit - credit) > 0.01;
    });

    const linesWithoutAccountObj = entriesForBalance.flatMap(e => 
      e.lines.filter(l => !accounts.find(a => a.id === l.accountId)).map(l => ({ entryId: e.id, entryDesc: e.description, date: e.date, line: l }))
    );

    const childCategoryMismatches = accounts.filter(a => {
      if (!a.parentId) return false;
      const parent = accounts.find(p => p.id === a.parentId);
      return parent && parent.category !== a.category;
    });

    const linesToParentAccounts = entriesForBalance.flatMap(e => 
      e.lines.filter(l => accounts.some(a => a.parentId === l.accountId)).map(l => ({ entryId: e.id, entryDesc: e.description, date: e.date, line: l }))
    );

    const diagnostic = {
      initialActivo,
      initialPasivo,
      initialPatrimonio,
      initialIngreso,
      initialEgreso,
      unbalancedEntriesCount: unbalancedEntriesInfo.length,
      unbalancedEntries: unbalancedEntriesInfo,
      linesWithoutLinkedAccount: linesWithoutAccountObj.length,
      linesWithoutLinkedAccountEntries: linesWithoutAccountObj,
      accountsMissingCategoryCount: accountsWithoutCategory.length,
      accountsMissingCategory: accountsWithoutCategory,
      hasSetupImbalance: setupImbalance,
      categoryMismatchesCount: childCategoryMismatches.length,
      categoryMismatches: childCategoryMismatches,
      linesToParentCount: linesToParentAccounts.length,
      linesToParentEntries: linesToParentAccounts
    };
    
    if (Math.abs(bsResult.activo - (bsResult.pasivo + bsResult.patrimonio + cumulativeNetIncome)) > 0.01) {
      console.log('--- DIAGNOSTICO DE DESCUADRE ---');
      console.log('Equilibrado (Falso):', 'Activo:', bsResult.activo, 'Pasivo:', bsResult.pasivo, 'Patrimonio:', bsResult.patrimonio, 'NetIncome:', cumulativeNetIncome);
      console.log('Diagnostic:', diagnostic);
      console.log('Initial Balances => Activo:', initialActivo, 'Pasivo:', initialPasivo, 'Patrimonio:', initialPatrimonio, 'Ingreso:', initialIngreso, 'Egreso:', initialEgreso);
      console.log('Setup Imbalance math:', Math.abs(initialActivo - (initialPasivo + initialPatrimonio + initialIngreso - initialEgreso)));
      
      const unbalAccounts = accounts.filter(a => a.initialBalance);
      if (unbalAccounts.length > 0) {
        console.log('Cuentas con saldo inicial explícito:', unbalAccounts.map(a => `${a.code} ${a.name}: ${a.initialBalance}`));
      }
      
      if (diagnostic.unbalancedEntriesCount > 0) {
        console.log('Asientos desbalaceados:', unbalancedEntriesInfo.map(e => ({date: e.date, id: e.id, description: e.description})));
      }
    }

    return {
      pnl: pnlResult,
      netIncome,
      cumulativeNetIncome,
      bs: bsResult,
      diagnostic
    };
  }, [entries, accounts, periodType, selectedMonth, dateRange, getFilteredEntries]);

  const renderReportTable = () => {
    if (reportType === 'P&L') {
      const incomes = financialData.pnl.items.filter(i => i.category === 'INGRESO').sort((a,b) => a.code.localeCompare(b.code));
      const expenses = financialData.pnl.items.filter(i => i.category === 'EGRESO').sort((a,b) => a.code.localeCompare(b.code));

      return (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2">
          {/* Summary Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-emerald-500/10 border border-emerald-500/20 p-6 rounded-2xl">
              <div className="flex items-center gap-3 mb-2 text-emerald-500">
                <TrendingUp size={20} />
                <span className="text-[10px] uppercase font-bold tracking-widest">Total Ingresos</span>
              </div>
              <div className="text-2xl font-bold text-white font-mono">{formatCurrency(financialData.pnl.ingresos)}</div>
            </div>
            <div className="bg-red-500/10 border border-red-500/20 p-6 rounded-2xl">
              <div className="flex items-center gap-3 mb-2 text-red-500">
                <TrendingDown size={20} />
                <span className="text-[10px] uppercase font-bold tracking-widest">Total Egresos</span>
              </div>
              <div className="text-2xl font-bold text-white font-mono">{formatCurrency(financialData.pnl.egresos)}</div>
            </div>
            <div className={`p-6 rounded-2xl border ${financialData.netIncome >= 0 ? 'bg-indigo-500/10 border-indigo-500/20' : 'bg-red-900/10 border-red-500/20'}`}>
              <div className={`flex items-center gap-3 mb-2 ${financialData.netIncome >= 0 ? 'text-indigo-400' : 'text-red-400'}`}>
                {financialData.netIncome >= 0 ? <CheckCircle2 size={20} /> : <AlertCircle size={20} />}
                <span className="text-[10px] uppercase font-bold tracking-widest">Utilidad / Pérdida Neto</span>
              </div>
              <div className={`text-2xl font-bold font-mono ${financialData.netIncome >= 0 ? 'text-indigo-400' : 'text-red-400'}`}>
                {formatCurrency(financialData.netIncome)}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-4">
              <h3 className="text-sm font-bold text-emerald-400 uppercase tracking-widest px-2">Detalle de Ingresos</h3>
              <div className="bg-zinc-800/20 border border-zinc-800 rounded-2xl divide-y divide-zinc-800/50">
                {incomes.map(item => (
                  <div key={item.id} className="flex items-center justify-between p-3" style={{ paddingLeft: `${item.level * 12}px` }}>
                    <div className="flex items-center gap-3 overflow-hidden">
                      <span className="text-[10px] font-mono text-zinc-500 w-16 shrink-0">{item.code}</span>
                      <span className={`text-xs truncate ${!item.isSelectable ? 'font-bold text-zinc-300' : 'text-zinc-400'}`}>
                        {item.name}
                      </span>
                    </div>
                    <span className={`text-xs font-mono font-bold ${item.balance > 0 ? 'text-zinc-200' : 'text-zinc-500'}`}>
                      {formatCurrency(item.balance)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-4">
              <h3 className="text-sm font-bold text-red-400 uppercase tracking-widest px-2">Detalle de Egresos</h3>
              <div className="bg-zinc-800/20 border border-zinc-800 rounded-2xl divide-y divide-zinc-800/50">
                {expenses.map(item => (
                  <div key={item.id} className="flex items-center justify-between p-3" style={{ paddingLeft: `${item.level * 12}px` }}>
                    <div className="flex items-center gap-3 overflow-hidden">
                      <span className="text-[10px] font-mono text-zinc-500 w-16 shrink-0">{item.code}</span>
                      <span className={`text-xs truncate ${!item.isSelectable ? 'font-bold text-zinc-300' : 'text-zinc-400'}`}>
                        {item.name}
                      </span>
                    </div>
                    <span className={`text-xs font-mono font-bold ${item.balance > 0 ? 'text-zinc-200' : 'text-zinc-500'}`}>
                      {formatCurrency(item.balance)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Balance Sheet
    const assets = financialData.bs.items.filter(i => i.category === 'ACTIVO').sort((a,b) => a.code.localeCompare(b.code));
    const totalPasivoPatrimonio = financialData.bs.pasivo + financialData.bs.patrimonio + financialData.cumulativeNetIncome;
    const isBalanced = Math.abs(financialData.bs.activo - totalPasivoPatrimonio) < 0.01;

    return (
      <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2">
        {/* Verification Alert */}
        <div className={`p-4 rounded-2xl border flex flex-col gap-4 ${isBalanced ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-red-500/5 border-red-500/20'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-xl scale-75 ${isBalanced ? 'bg-emerald-500 text-black' : 'bg-red-500 text-white'}`}>
                <Scale size={24} />
              </div>
              <div>
                <p className={`text-xs font-bold uppercase tracking-wider ${isBalanced ? 'text-emerald-500' : 'text-red-500'}`}>
                  {isBalanced ? 'Ecuación Patrimonial Equilibrada' : 'Error en Ecuación Patrimonial'}
                </p>
                <p className="text-zinc-400 text-[11px] font-mono">
                  {formatCurrency(financialData.bs.activo)} (Activo) = {formatCurrency(financialData.bs.pasivo)} (Pasivo) + {formatCurrency(financialData.bs.patrimonio + financialData.cumulativeNetIncome)} (Patrimonio)
                </p>
              </div>
            </div>
            {isBalanced ? (
              <span className="bg-emerald-500/20 text-emerald-500 text-[10px] px-2 py-1 rounded-full font-bold">CONCILIADO</span>
            ) : (
              <span className="bg-red-500/20 text-red-500 text-[10px] px-2 py-1 rounded-full font-bold animate-pulse">DESEQUILIBRADO</span>
            )}
          </div>
          
          {!isBalanced && (
            <div className="bg-black/20 rounded-xl p-4 mt-2 border border-red-500/20">
              <p className="text-xs font-bold text-red-400 mb-2 uppercase tracking-wide">Diagnóstico de Desequilibrio:</p>
              <ul className="list-disc pl-4 space-y-2 text-xs text-zinc-300">
                {financialData.diagnostic.hasSetupImbalance && (
                  <li>
                    <strong>Saldos Iniciales no equitativos:</strong> La suma matemática de todos los saldos iniciales cargados manualmente en tu Plan de Cuentas (Activo ≈ {formatCurrency(financialData.diagnostic.initialActivo)}, Pasivo ≈ {formatCurrency(financialData.diagnostic.initialPasivo)}, Patrimonio ≈ {formatCurrency(financialData.diagnostic.initialPatrimonio)}) no balancea la ecuación (Activo = Pasivo + Patrimonio).
                    <div className="mt-2 bg-zinc-900 rounded p-2 text-[10px] sm:text-xs">
                      <p className="text-zinc-500 mb-1">Cuentas que contienen un Saldo Inicial asignado:</p>
                      {accounts.filter(a => a.initialBalance).map(a => (
                        <div key={a.id} className="flex justify-between items-center py-0.5">
                          <span className="text-zinc-400 font-mono">{a.code} {a.name}</span>
                          <span className="text-zinc-300 font-bold">{formatCurrency(a.initialBalance || 0)}</span>
                        </div>
                      ))}
                      {accounts.filter(a => a.initialBalance).length === 0 && <span className="text-emerald-500">Ninguna cuenta encontrada con Saldo Inicial.</span>}
                    </div>
                  </li>
                )}
                {financialData.diagnostic.unbalancedEntriesCount > 0 && (
                  <li>
                    <strong>Asientos Desbalanceados:</strong> Existen {financialData.diagnostic.unbalancedEntriesCount} asientos donde el Debe no es igual al Haber.
                    <div className="mt-2 bg-zinc-900 rounded p-2 text-[10px] sm:text-xs">
                      {financialData.diagnostic.unbalancedEntries.map(e => (
                        <div key={e.id} className="flex flex-col gap-1 py-1 border-b border-zinc-800 last:border-0 text-[10px] sm:text-xs">
                          <div className="flex justify-between items-start">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-zinc-400">{safeFormat(e.date, 'dd/MM/yyyy')}</span>
                              <span className="text-indigo-400 bg-indigo-500/10 px-1 py-0.5 rounded font-mono font-bold text-[9px]">
                                #{e.id.slice(-4).toUpperCase()}
                              </span>
                              {e.documentLocatorCode ? (
                                <span className="text-black bg-white px-1.5 py-0.5 rounded font-mono font-bold text-[9px] shadow-sm">
                                  REF: {e.documentLocatorCode}
                                </span>
                              ) : (
                                <span className="text-zinc-600 bg-zinc-800/50 px-1 py-0.5 rounded font-bold text-[8px] uppercase">
                                  S/Ref
                                </span>
                              )}
                              <span className="text-zinc-300 font-medium truncate max-w-[200px] sm:max-w-md">{e.description}</span>
                            </div>
                            <span className="text-red-400 font-bold whitespace-nowrap text-[10px]">Asiento Inválido</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </li>
                )}
                {financialData.diagnostic.linesWithoutLinkedAccount > 0 && (
                  <li>
                    <strong>Cuentas Inexistentes:</strong> Se detectaron {financialData.diagnostic.linesWithoutLinkedAccount} líneas de asientos apuntando a cuentas que ya no existen en el plan de cuentas.
                    <div className="mt-2 bg-zinc-900 rounded p-2 text-[10px] sm:text-xs max-h-32 overflow-y-auto">
                      {Array.from(new Set(financialData.diagnostic.linesWithoutLinkedAccountEntries.map(e => `${e.date} - ${e.entryDesc} -> ID Inexistente: ${e.line.accountId}`))).map((desc, idx) => (
                        <div key={idx} className="flex items-start gap-2 py-0.5">
                          <span className="text-red-400 mt-0.5">&bull;</span>
                          <span className="text-zinc-300">{desc}</span>
                        </div>
                      ))}
                    </div>
                  </li>
                )}
                {financialData.diagnostic.accountsMissingCategoryCount > 0 && (
                  <li>
                    <strong>Cuentas sin Categoría:</strong> Hay {financialData.diagnostic.accountsMissingCategoryCount} cuentas de nivel raíz que no tienen categoría contable (ACTIVO, PASIVO, etc).
                    <div className="mt-2 bg-zinc-900 rounded p-2 text-[10px] sm:text-xs text-zinc-400">
                       {financialData.diagnostic.accountsMissingCategory.join(', ')}
                    </div>
                  </li>
                )}
                {financialData.diagnostic.categoryMismatchesCount > 0 && (
                  <li>
                    <div className="flex justify-between items-start">
                      <div>
                        <strong>Problema de Jerarquía:</strong> Hemos detectado {financialData.diagnostic.categoryMismatchesCount} cuenta(s) cuya categoría no coincide con la de su cuenta 'padre'.
                      </div>
                      <div className="flex flex-col gap-2 shrink-0 ml-4">
                        {!showFixCategoriesConfirm ? (
                          <button 
                            onClick={() => setShowFixCategoriesConfirm(true)}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white text-[10px] sm:text-xs px-3 py-1 rounded-full transition-colors font-medium self-end"
                          >
                            Corregir Automáticamente
                          </button>
                        ) : (
                          <div className="bg-zinc-800 border border-zinc-700 p-2 text-xs rounded-xl flex flex-col gap-2 items-center">
                            <span className="font-medium text-white mb-1">
                              ¿Corregir {financialData.diagnostic.categoryMismatchesCount} cuenta(s)?
                            </span>
                            <div className="flex gap-2 w-full justify-center">
                              <button 
                                onClick={() => { setShowFixCategoriesConfirm(false); setFixCategoriesError(null); }}
                                disabled={isFixingCategories}
                                className="bg-zinc-700 hover:bg-zinc-600 text-zinc-300 px-3 py-1 rounded-full transition-colors"
                              >
                                Cancelar
                              </button>
                              <button 
                                onClick={async () => {
                                  setIsFixingCategories(true);
                                  setFixCategoriesError(null);
                                  try {
                                    for (const a of financialData.diagnostic.categoryMismatches) {
                                      const parent = accounts.find(p => p.id === a.parentId);
                                      if (parent) {
                                        await updateAccountingAccount(user!.uid, a.id, { category: parent.category });
                                      }
                                    }
                                    setShowFixCategoriesConfirm(false);
                                  } catch(err) {
                                    console.error(err);
                                    setFixCategoriesError("Error al corregir");
                                  } finally {
                                    setIsFixingCategories(false);
                                  }
                                }}
                                disabled={isFixingCategories}
                                className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1 rounded-full transition-colors font-medium flex items-center gap-1 disabled:opacity-50"
                              >
                                {isFixingCategories ? <span className="w-3 h-3 border-2 border-white/20 border-t-white rounded-full animate-spin"></span> : null}
                                Confirmar
                              </button>
                            </div>
                            {fixCategoriesError && <span className="text-red-400 text-[10px] mt-1">{fixCategoriesError}</span>}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="mt-2 bg-zinc-900 rounded p-2 text-[10px] sm:text-xs">
                      {financialData.diagnostic.categoryMismatches.map(a => {
                        const parent = accounts.find(p => p.id === a.parentId);
                        return (
                          <div key={a.id} className="flex flex-col py-1 border-b border-zinc-800 last:border-0">
                            <div className="flex justify-between items-center text-zinc-300">
                               <span className="font-mono font-bold text-red-400">{a.code} {a.name}</span>
                               <span className="text-zinc-500 line-through">{a.category}</span>
                            </div>
                            {parent && (
                              <div className="flex justify-between items-center text-zinc-500 mt-1 pl-4">
                                <span>Padre: {parent.code} {parent.name}</span>
                                <span className="text-emerald-400 font-bold">{parent.category}</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </li>
                )}
                {financialData.diagnostic.linesToParentCount > 0 && (
                  <li>
                    <strong>Asientos apuntando a Cuentas Sumarizadoras:</strong> Hay {financialData.diagnostic.linesToParentCount} línea(s) en asientos que apuntan a cuentas que tienen sub-cuentas. Esto rompe la jerarquía, ya que solo las cuentas "hoja" (último nivel) pueden recibir movimientos.
                    <div className="mt-2 bg-zinc-900 rounded p-2 text-[10px] sm:text-xs max-h-40 overflow-y-auto">
                      {Array.from(new Set(financialData.diagnostic.linesToParentEntries.map(e => {
                        const acc = accounts.find(a => a.id === e.line.accountId);
                        const accLabel = acc ? `[${acc.code}] ${acc.name}` : `ID: ${e.line.accountId}`;
                        return `${e.date} - ${e.entryDesc} -> ${accLabel}`;
                      }))).map((desc, idx) => (
                        <div key={idx} className="flex items-start gap-2 py-0.5">
                          <span className="text-orange-400 mt-0.5">&bull;</span>
                          <span className="text-zinc-300">{desc}</span>
                        </div>
                      ))}
                    </div>
                  </li>
                )}
                {!financialData.diagnostic.hasSetupImbalance && financialData.diagnostic.unbalancedEntriesCount === 0 && financialData.diagnostic.linesWithoutLinkedAccount === 0 && financialData.diagnostic.accountsMissingCategoryCount === 0 && financialData.diagnostic.categoryMismatchesCount === 0 && financialData.diagnostic.linesToParentCount === 0 && (
                  <li>No se detectaron errores de sistema ni de integridad. El error podría ocurrir si los códigos jerárquicos o los tipos de cuentas (Activo, Pasivo) están configurados fuera del estandar (por ejemplo, tener un Pasivo que no resta). ¡Verifica la estructura de tu Plan de Cuentas!</li>
                )}
              </ul>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Activo */}
          <div className="space-y-4">
            <div className="flex items-center justify-between px-2">
              <h3 className="text-sm font-bold text-white uppercase tracking-widest">Activo</h3>
              <span className="text-sm font-mono font-bold text-indigo-400">{formatCurrency(financialData.bs.activo)}</span>
            </div>
            <div className="bg-zinc-800/20 border border-zinc-800 rounded-2xl divide-y divide-zinc-800/50">
              {assets.map(item => (
                <div key={item.id} className="flex items-center justify-between p-3" style={{ paddingLeft: `${item.level * 12}px` }}>
                  <div className="flex items-center gap-3 overflow-hidden">
                    <span className="text-[10px] font-mono text-zinc-500 w-16 shrink-0">{item.code}</span>
                    <span className={`text-xs truncate ${!item.isSelectable ? 'font-bold text-zinc-300' : 'text-zinc-400'}`}>
                      {item.name}
                    </span>
                  </div>
                  <span className={`text-xs font-mono font-bold ${item.balance !== 0 ? 'text-zinc-200' : 'text-zinc-500'}`}>
                    {formatCurrency(item.balance)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-8">
            {/* Pasivo */}
            <div className="space-y-4">
              <div className="flex items-center justify-between px-2">
                <h3 className="text-sm font-bold text-white uppercase tracking-widest">Pasivo</h3>
                <span className="text-sm font-mono font-bold text-indigo-400">{formatCurrency(financialData.bs.pasivo)}</span>
              </div>
              <div className="bg-zinc-800/20 border border-zinc-800 rounded-2xl divide-y divide-zinc-800/50">
                {financialData.bs.items.filter(i => i.category === 'PASIVO').sort((a,b) => a.code.localeCompare(b.code)).map(item => (
                  <div key={item.id} className="flex items-center justify-between p-3" style={{ paddingLeft: `${item.level * 12}px` }}>
                    <div className="flex items-center gap-3 overflow-hidden">
                      <span className="text-[10px] font-mono text-zinc-500 w-16 shrink-0">{item.code}</span>
                      <span className={`text-xs truncate ${!item.isSelectable ? 'font-bold text-zinc-300' : 'text-zinc-400'}`}>
                        {item.name}
                      </span>
                    </div>
                    <span className={`text-xs font-mono font-bold ${item.balance !== 0 ? 'text-zinc-200' : 'text-zinc-500'}`}>
                      {formatCurrency(item.balance)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Patrimonio */}
            <div className="space-y-4">
              <div className="flex items-center justify-between px-2">
                <h3 className="text-sm font-bold text-white uppercase tracking-widest">Patrimonio Neto</h3>
                <span className="text-sm font-mono font-bold text-indigo-400">{formatCurrency(financialData.bs.patrimonio + financialData.cumulativeNetIncome)}</span>
              </div>
              <div className="bg-zinc-800/20 border border-zinc-800 rounded-2xl divide-y divide-zinc-800/50">
                {financialData.bs.items.filter(i => i.category === 'PATRIMONIO').sort((a,b) => a.code.localeCompare(b.code)).map(item => (
                  <div key={item.id} className="flex items-center justify-between p-3" style={{ paddingLeft: `${item.level * 12}px` }}>
                    <div className="flex items-center gap-3 overflow-hidden">
                      <span className="text-[10px] font-mono text-zinc-500 w-16 shrink-0">{item.code}</span>
                      <span className={`text-xs truncate ${!item.isSelectable ? 'font-bold text-zinc-300' : 'text-zinc-400'}`}>
                        {item.name}
                      </span>
                    </div>
                    <span className={`text-xs font-mono font-bold ${item.balance !== 0 ? 'text-zinc-200' : 'text-zinc-500'}`}>
                      {formatCurrency(item.balance)}
                    </span>
                  </div>
                ))}
                {/* Automation: Result of Exercise */}
                <div className="flex items-center justify-between p-3 border-t border-indigo-500/20 bg-indigo-500/5">
                  <div className="flex items-center gap-3 overflow-hidden">
                    <span className="text-[10px] font-mono text-indigo-500 w-16 shrink-0">AUTO</span>
                    <span className="text-xs font-bold text-indigo-300">RESULTADO DEL EJERCICIO (P&L)</span>
                  </div>
                  <span className={`text-xs font-mono font-bold ${financialData.cumulativeNetIncome >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {formatCurrency(financialData.cumulativeNetIncome)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="bg-zinc-900 border border-zinc-800 rounded-3xl p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex bg-zinc-800 p-1.5 rounded-2xl w-fit">
            <button 
              onClick={() => setReportType('BALANCE')}
              className={`px-6 py-2 rounded-xl text-xs font-bold transition-all ${
                reportType === 'BALANCE' ? 'bg-zinc-700 text-white shadow-lg' : 'text-zinc-500 hover:text-zinc-300'
              }`}
            >
              Balance General
            </button>
            <button 
              onClick={() => setReportType('P&L')}
              className={`px-6 py-2 rounded-xl text-xs font-bold transition-all ${
                reportType === 'P&L' ? 'bg-zinc-700 text-white shadow-lg' : 'text-zinc-500 hover:text-zinc-300'
              }`}
            >
              Estado de Resultados
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <div className="flex bg-zinc-950 p-1 rounded-xl">
              <button 
                onClick={() => setPeriodType('MONTH')}
                className={`p-1.5 rounded-lg transition-all ${periodType === 'MONTH' ? 'bg-zinc-800 text-white' : 'text-zinc-500 hover:text-zinc-400'}`}
                title="Mensual"
              >
                <Calendar size={18} />
              </button>
              <button 
                onClick={() => setPeriodType('RANGE')}
                className={`p-1.5 rounded-lg transition-all ${periodType === 'RANGE' ? 'bg-zinc-800 text-white' : 'text-zinc-500 hover:text-zinc-400'}`}
                title="Rango"
              >
                <Filter size={18} />
              </button>
              <button 
                onClick={() => setPeriodType('CUMULATIVE')}
                className={`p-1.5 rounded-lg transition-all ${periodType === 'CUMULATIVE' ? 'bg-zinc-800 text-white' : 'text-zinc-500 hover:text-zinc-400'}`}
                title="Acumulado"
              >
                <History size={18} />
              </button>
            </div>

            {periodType === 'MONTH' && (
              <input 
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="bg-zinc-800 border border-zinc-700 rounded-xl px-4 py-2 text-white text-xs outline-none focus:border-indigo-500 transition-colors"
              />
            )}

            {periodType === 'RANGE' && (
              <div className="flex items-center gap-2">
                <input 
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                  className="bg-zinc-800 border border-zinc-700 rounded-xl px-4 py-2 text-white text-xs"
                />
                <span className="text-zinc-600">to</span>
                <input 
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                  className="bg-zinc-800 border border-zinc-700 rounded-xl px-4 py-2 text-white text-xs"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="min-h-[500px]">
        {renderReportTable()}
      </div>
    </div>
  );
};

const History = ({ size, className }: any) => (
    <svg 
      width={size} 
      height={size} 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className={className}
    >
      <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
      <path d="M3 3v5h5" />
      <path d="M12 7v5l4 2" />
    </svg>
);
